// frontend/src/utils/eventTimeUtils.js
// Timezone-aware time formatting utilities for events

/**
 * Get the user's local IANA timezone string.
 */
export function getUserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "Europe/Zurich";
  }
}

/**
 * Format a time in a specific timezone.
 * @param {string} dateString - ISO date string from the API
 * @param {string} locale - 'de-DE' or 'en-US'
 * @param {string} timeZone - IANA timezone (e.g., 'Europe/Zurich')
 * @returns {string} e.g., "19:30"
 */
export function formatTimeInZone(dateString, locale, timeZone) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  });
}

/**
 * Get the short timezone abbreviation for a specific timezone at a specific date.
 * @param {string} dateString - ISO date string (needed to determine DST)
 * @param {string} locale - 'de-DE' or 'en-US'
 * @param {string} timeZone - IANA timezone
 * @returns {string} e.g., "CET", "CEST", "SAST"
 */
export function getTimezoneAbbreviation(dateString, locale, timeZone) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone,
    timeZoneName: "short",
  }).formatToParts(date);
  const tzPart = parts.find((p) => p.type === "timeZoneName");
  return tzPart ? tzPart.value : timeZone;
}

/**
 * Check if two IANA timezone strings have the same offset at a given moment.
 * @param {string} dateString - ISO date string
 * @param {string} tz1 - IANA timezone
 * @param {string} tz2 - IANA timezone
 * @returns {boolean}
 */
export function isSameTimezoneOffset(dateString, tz1, tz2) {
  if (tz1 === tz2) return true;
  if (!dateString) return false;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return false;
  const getOffset = (tz) => {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "longOffset",
    }).formatToParts(date);
    const offsetPart = parts.find((p) => p.type === "timeZoneName");
    return offsetPart ? offsetPart.value : "";
  };
  return getOffset(tz1) === getOffset(tz2);
}

/**
 * Compute the live/starting-soon/past status of an event.
 * @param {Object} event - { start_datetime, end_datetime }
 * @param {Date} [now] - Optional current time (for timer-driven re-evaluation)
 * @returns {{ isLive: boolean, isStartingSoon: boolean, isPast: boolean, minutesUntilStart: number|null }}
 */
export function getEventLiveStatus(event, now = new Date()) {
  const start = new Date(event.start_datetime);
  const end = new Date(event.end_datetime);

  const isPast = now >= end;
  const isLive = now >= start && now < end;
  const msUntilStart = start - now;
  const minutesUntilStart =
    msUntilStart > 0 ? Math.ceil(msUntilStart / 60000) : null;
  const isStartingSoon =
    !isLive && !isPast && minutesUntilStart !== null && minutesUntilStart <= 30;

  return { isLive, isStartingSoon, isPast, minutesUntilStart };
}

/**
 * Build complete time display data for an event.
 * @param {Object} event - Event with start_datetime, end_datetime, timezone
 * @param {string} language - 'de' or 'en'
 * @returns {Object} { eventTimeLine, localTimeLine, showLocalTime }
 */
export function getEventTimeDisplay(event, language) {
  const locale = language === "de" ? "de-DE" : "en-US";
  const eventTz = event.timezone || "Europe/Zurich";
  const userTz = getUserTimezone();

  const startInEventTz = formatTimeInZone(event.start_datetime, locale, eventTz);
  const endInEventTz = formatTimeInZone(event.end_datetime, locale, eventTz);
  const eventTzAbbr = getTimezoneAbbreviation(event.start_datetime, locale, eventTz);

  const sameTimezone = isSameTimezoneOffset(event.start_datetime, eventTz, userTz);

  let localTimeLine = null;
  if (!sameTimezone) {
    const startInUserTz = formatTimeInZone(event.start_datetime, locale, userTz);
    const endInUserTz = formatTimeInZone(event.end_datetime, locale, userTz);
    const userTzAbbr = getTimezoneAbbreviation(event.start_datetime, locale, userTz);
    localTimeLine = {
      start: startInUserTz,
      end: endInUserTz,
      abbreviation: userTzAbbr,
      label: language === "de" ? "deine Zeit" : "your time",
    };
  }

  return {
    eventTimeLine: {
      start: startInEventTz,
      end: endInEventTz,
      abbreviation: eventTzAbbr,
    },
    localTimeLine,
    showLocalTime: !sameTimezone,
  };
}
